import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { buildCloudinaryImageUrl } from "../../utils/CloudinaryImageUrl";

export default function CategoryCarousel({
    onSelectCategory,

    categories, menuType
}) {
    const { t } = useTranslation();

    const ctViewportRef = useRef(null);
    const ctTrackRef = useRef(null);

    const [ctPage, setCtPage] = useState(0);
    const [ctPages, setCtPages] = useState(1);

    // drag con mouse (en táctil se usa el scroll nativo)
    const ctIsDown = useRef(false);
    const ctStartX = useRef(0);
    const ctStartScroll = useRef(0);
    const ctDragDistance = useRef(0);

    const ctClamp = (n, min, max) => Math.max(min, Math.min(max, n));

    // mide sobre el ancho real de la tarjeta, no sobre el viewport
    const ctMetrics = () => {
        const viewport = ctViewportRef.current;
        const track = ctTrackRef.current;
        if (!viewport || !track) return null;

        const first = track.querySelector(".ct-category-card");
        const cardW = first?.getBoundingClientRect()?.width || 300;
        const gap = parseFloat(window.getComputedStyle(track).gap || "16") || 16;

        const step = cardW + gap;
        const perView = Math.max(1, Math.floor((viewport.clientWidth + gap + 1) / step));

        return { viewport, step, perView, pageW: step * perView };
    };

    const ctCalcPages = () => {
        const m = ctMetrics();
        if (!m) return;

        const pages = Math.max(1, Math.ceil(categories.length / m.perView));
        setCtPages(pages);
        setCtPage((p) => ctClamp(p, 0, pages - 1));
    };

    useEffect(() => {
        ctCalcPages();
        window.addEventListener("resize", ctCalcPages);
        return () => window.removeEventListener("resize", ctCalcPages);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categories.length]);

    const ctScrollToPage = (p) => {
        const m = ctMetrics();
        if (!m) return;
        m.viewport.scrollTo({ left: p * m.pageW, behavior: "smooth" });
        setCtPage(p);
    };

    // el indicador sigue al scroll nativo (dedo, trackpad, rueda)
    useEffect(() => {
        const viewport = ctViewportRef.current;
        if (!viewport) return;

        const onScroll = () => {
            const m = ctMetrics();
            if (!m) return;

            const atEnd = viewport.scrollLeft + viewport.clientWidth >= viewport.scrollWidth - 2;
            const p = atEnd ? ctPages - 1 : Math.round(viewport.scrollLeft / m.pageW);

            setCtPage((prev) => (prev === p ? prev : ctClamp(p, 0, ctPages - 1)));
        };

        viewport.addEventListener("scroll", onScroll, { passive: true });
        return () => viewport.removeEventListener("scroll", onScroll);
    }, [ctPages]);

    const ctOnMouseDown = (e) => {
        const viewport = ctViewportRef.current;
        if (!viewport) return;
        ctIsDown.current = true;
        ctDragDistance.current = 0;
        ctStartX.current = e.pageX;
        ctStartScroll.current = viewport.scrollLeft;
        viewport.classList.add("ct-dragging");
    };

    const ctOnMouseMove = (e) => {
        const viewport = ctViewportRef.current;
        if (!viewport || !ctIsDown.current) return;
        const dx = e.pageX - ctStartX.current;
        ctDragDistance.current = Math.abs(dx);
        viewport.scrollLeft = ctStartScroll.current - dx;
    };

    const ctStopDrag = () => {
        const viewport = ctViewportRef.current;
        if (!viewport || !ctIsDown.current) return;

        ctIsDown.current = false;
        viewport.classList.remove("ct-dragging");

        // acomoda a la tarjeta más cercana para que nunca quede cortada
        const m = ctMetrics();
        if (!m) return;
        viewport.scrollTo({
            left: Math.round(viewport.scrollLeft / m.step) * m.step,
            behavior: "smooth"
        });
    };

    const ctHandleSelect = (cat) => onSelectCategory?.(cat);

    const ctPrevDisabled = ctPage <= 0;
    const ctNextDisabled = ctPage >= ctPages - 1;

    const navigate = useNavigate();
    const selectCategorie = (categorie) => {
        // si venía arrastrando, no cuenta como click
        if (ctDragDistance.current > 6) return;

        localStorage.setItem('selectedCategorie', JSON.stringify(categorie))
        if (menuType == "points") {
            navigate(`/menu/?type=points`)
        } else {
            navigate(`/menu`)
        }
    }

    return (
        <section className="ct-categories" aria-label={t('home.categoriesAria')}>

            <div className="ct-categories-head">
                <div className="ct-categories-titlewrap">
                    <p className="fv-section-label">{t('home.categoriesLabel')}</p>
                    <h2 className="fv-section-title" style={{ marginBottom: "2rem" }}>
                        {t('home.categoriesTitleLine1')}<br />{t('home.categoriesTitleLine2')}
                    </h2>
                </div>

                <div className="ct-categories-actions">
                    <button
                        type="button"
                        className="ct-cat-arrow"
                        onClick={() => ctScrollToPage(ctClamp(ctPage - 1, 0, ctPages - 1))}
                        disabled={ctPrevDisabled}
                        aria-label={t('common.previous')}
                        title={t('common.previous')}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M15 18l-6-6 6-6" />
                        </svg>
                    </button>

                    <button
                        type="button"
                        className="ct-cat-arrow"
                        onClick={() => ctScrollToPage(ctClamp(ctPage + 1, 0, ctPages - 1))}
                        disabled={ctNextDisabled}
                        aria-label={t('common.next')}
                        title={t('common.next')}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M9 18l6-6-6-6" />
                        </svg>
                    </button>
                </div>
            </div>

            <div
                className="ct-cat-viewport"
                ref={ctViewportRef}
                onMouseDown={ctOnMouseDown}
                onMouseMove={ctOnMouseMove}
                onMouseUp={ctStopDrag}
                onMouseLeave={ctStopDrag}
                role="region"
                aria-roledescription="carousel"
                aria-label={t('home.categoriesCarouselAria')}
            >
                <div className="ct-cat-track" ref={ctTrackRef}>
                    {categories.map((cat) => (
                        <button
                            key={cat.id}
                            type="button"
                            className="ct-category-card"
                            onClick={() => selectCategorie(cat)}
                            title={cat.category_name}

                            id={cat.category_id + "categorie"}
                        >
                            <img className="ct-category-img" alt={cat.alt}
                                src={buildCloudinaryImageUrl({ url: cat.img, width: 300, quality: "auto", type: "image" })}
                                onError={e => e.target.style.opacity = 0} loading="lazy"
                                draggable={false}
                            />

                            <div className="ct-category-overlay" aria-hidden="true" />
                            <div className="ct-category-content">
                                <strong className="ct-category-name">{cat.category_name}</strong>
                                <span className="ct-category-desc">{cat.desc}</span>
                                <span className="ct-category-cta">
                                    {t('home.seeOptions')}
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                        <path d="M5 12h14" />
                                        <path d="M12 5l7 7-7 7" />
                                    </svg>
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <div className="ct-cat-dots" aria-label={t('home.pagesAria')}>
                {Array.from({ length: ctPages }).map((_, i) => (
                    <button
                        key={i}
                        type="button"
                        className={`ct-cat-dot ${i === ctPage ? "ct-active" : ""}`}
                        aria-label={t('home.pageAria', { n: i + 1 })}
                        onClick={() => ctScrollToPage(i)}
                    />
                ))}
            </div>
        </section>
    );
}